import { View, Text } from 'react-native'
import { Pressable } from '../ui/Pressable'
import Animated, { FadeInDown } from 'react-native-reanimated'
import { useRouter } from 'expo-router'
import { selection } from '@/lib/utils/haptics'
import { ChevronRight } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '../ui/Checkbox'
import { useAnimatedPress } from '../../lib/hooks/use-animated-press'
import { useSemanticColors, SPACING, DURATION, INTERACTION, STAGGER } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { FONTS } from '../../lib/brand'
import type { ConsentValues } from '../../lib/types/auth.types'
import { useEntrance } from '@/lib/hooks/use-entrance'

interface ConsentFormProps {
  values: ConsentValues
  onChange: (values: ConsentValues) => void
  errors?: Partial<Record<keyof ConsentValues, string>>
  testIDPrefix?: string
}

function ConsentLink({ label, route }: { label: string; route: string }) {
  // This used to pin `useSemanticColors(false)` from the era when the (auth)
  // group was light-only. register.tsx reads `useTheme()` today, so the pin
  // painted the two links in the LIGHT accent (#C72D1D) inside a dark screen,
  // one line under a checkbox label that had already followed the theme.
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()
  const {
    animatedStyle: scaleStyle,
    onPressIn,
    onPressOut,
  } = useAnimatedPress({ scaleTo: 0.96, haptic: false })

  return (
    <Animated.View style={scaleStyle}>
      <Pressable
        onPress={() => {
          void selection()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          router.push(route as any)
        }}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        accessibilityRole="link"
        accessibilityLabel={label}
        // hitSlop is dropped by react-native-web, so these two legal links
        // were a 17pt target on the build most people sign up on. The row
        // itself now carries the 44pt minimum.
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs,
          minHeight: INTERACTION.minTouchTarget,
        }}
      >
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 13,
            lineHeight: 18,
            color: colors.textAccent,
            textDecorationLine: 'underline',
            flexShrink: 1,
          }}
        >
          {label}
        </Text>
        {/* Same ink as the label it belongs to — the chevron used to be raw
            coral next to a darker accent word, two reds on one link. */}
        <ChevronRight size={14} color={colors.textAccent} />
      </Pressable>
    </Animated.View>
  )
}

export function ConsentForm({ values, onChange, errors, testIDPrefix }: ConsentFormProps) {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <View style={{ marginTop: SPACING.lg - SPACING.xs }}>
      <Animated.View collapsable={false} entering={enter()}>
        <Checkbox
          checked={values.acceptTerms}
          onChange={(v) => onChange({ ...values, acceptTerms: v })}
          required
          compact
          error={errors?.acceptTerms}
          testID={testIDPrefix ? `${testIDPrefix}.consent-terms` : undefined}
          label={
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textPrimary,
                flex: 1,
              }}
            >
              {t('consent.terms_and_privacy')}
            </Text>
          }
        />
      </Animated.View>
      <Animated.View
        collapsable={false}
        entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)}
        // Two 13pt labels plus chevrons measure wider than a phone column, and
        // a non-wrapping row with flexShrink:0 children overflowed it. They
        // wrap to one per line when they have to, side by side when they fit.
        // marginTop drops to xs because each link now carries 44pt of its own.
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          columnGap: SPACING.md,
          rowGap: SPACING.xs,
          marginTop: SPACING.xs,
        }}
      >
        <ConsentLink label={t('consent.view_terms')} route="/legal/signup-terms" />
        <ConsentLink label={t('consent.view_privacy')} route="/legal/privacy" />
      </Animated.View>
    </View>
  )
}
